import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";

const PENDING_BASE = API_ENDPOINT.WAREHOUSE.ITEM_INWARD.CHALLAN_ACCESSORY_INWARD;
const COMPLETED_BASE = API_ENDPOINT.WAREHOUSE.ITEM_INWARD.RETURNED_ACCESSORIES_GRN;

export const fetchChallanAccessoryInwardAnalytics = createAsyncThunk(
  "returnedAccessoriesInward/fetchAnalytics",
  async (warehouseId?: string) => {
    const response: any = await Axios.get(COMPLETED_BASE.ANALYTICS(warehouseId));
    return response?.data;
  },
);

export const fetchPendingChallans = createAsyncThunk(
  "returnedAccessoriesInward/fetchPendingChallans",
  async (params: any) => {
    let url = PENDING_BASE.PENDING_CHALLANS(params.warehouseId);
    if (params.page) url += `${url.includes("?") ? "&" : "?"}page=${params.page}`;
    if (params.pageSize) url += `&limit=${params.pageSize}`;
    if (params.search) url += `&search=${encodeURIComponent(params.search)}`;
    if (params.sort) url += `&sort=${params.sort}`;
    if (params.order) url += `&order=${params.order}`;

    const response: any = await Axios.get(url, {
      params: { filters: params.filters },
    });
    return response?.data;
  },
);

export const fetchPendingChallanDropdown = createAsyncThunk(
  "returnedAccessoriesInward/fetchPendingChallanDropdown",
  async ({ field, warehouseId }: { field: string; warehouseId?: string }) => {
    const response: any = await Axios.get(PENDING_BASE.FILTER_DROPDOWN(field, warehouseId));
    return response?.data;
  },
);

export const fetchInwardFormData = createAsyncThunk(
  "returnedAccessoriesInward/fetchInwardFormData",
  async ({ challanId, returnId }: { challanId: string; returnId?: string }) => {
    const response: any = await Axios.get(PENDING_BASE.INWARD_FORM(challanId, returnId));
    return response?.data;
  },
);

export const processAccessoryInward = createAsyncThunk(
  "returnedAccessoriesInward/process",
  async (
    { challanId, returnId, formData }: { challanId: string; returnId: string; formData: any },
    { rejectWithValue },
  ) => {
    try {
      const response: any = await Axios.post(PENDING_BASE.PROCESS(challanId, returnId), formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response?.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to process inward");
    }
  },
);

export const exportPendingRACSV = async ({
  warehouse_id,
  search,
  filters,
  sort,
  order
}: {
  warehouse_id: string;
  search?: string;
  filters?: {
    filterField: string;
    filterValue: any;
  }[];
  sort?: string;
  order?: string;
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);
  if (sort) params.append("sort", sort);
  if (order) params.append("order", order);

  const body = {
    filters: filters,
  };

  const response = await Axios.post(
    `${PENDING_BASE.EXPORT}?warehouse_id=${warehouse_id}&${params.toString()}`,
    body,
    { responseType: "blob" }
  );

  return {
    blob: response.data,
    filename: response.headers["content-disposition"]
      ? response.headers["content-disposition"].split("filename=")[1]
      : `Pending_RA_${new Date().toISOString().split("T")[0]}.csv`,
  };
};

export const exportCompletedRAGRNCSV = async ({
  warehouse_id,
  search,
  filters,
  sort,
  order
}: {
  warehouse_id: string;
  search?: string;
  filters?: {
    filterField: string;
    filterValue: any;
  }[];
  sort?: string;
  order?: string;
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);
  if (sort) params.append("sort", sort);
  if (order) params.append("order", order);

  const body = {
    filters: filters,
  };

  const response = await Axios.post(
    `${COMPLETED_BASE.EXPORT}?warehouse_id=${warehouse_id}&${params.toString()}`,
    body,
    { responseType: "blob" }
  );
  
  return {
    blob: response.data,
    filename: response.headers["content-disposition"]
      ? response.headers["content-disposition"].split("filename=")[1]
      : `Returned_Accessories_GRN_${new Date().toISOString().split("T")[0]}.csv`,
  };
};

export const fetchGRNListForChallan = createAsyncThunk(
  "returnedAccessoriesInward/fetchGRNList",
  async (challanId: string) => {
    const response: any = await Axios.get(COMPLETED_BASE.GRN_LIST(challanId));
    return response?.data;
  },
);

export const fetchAllInwards = createAsyncThunk(
  "returnedAccessoriesInward/fetchAllInwards",
  async (payload: any = {}) => {
    const { warehouseId, ...rest } = payload;
    let url = COMPLETED_BASE.ALL_INWARDS(warehouseId);
    if (Object.keys(rest).length > 0) {
      const queryString = Object.keys(rest)
        .map((key) => `${key}=${encodeURIComponent(typeof rest[key] === 'object' ? JSON.stringify(rest[key]) : rest[key])}`)
        .join('&');
      url += (url.includes('?') ? '&' : '?') + queryString;
    }
    const response: any = await Axios.get(url);
    return response?.data;
  },
);

export const fetchCompletedReturnedAccessoriesDropdown = createAsyncThunk(
  "returnedAccessoriesInward/fetchCompletedDropdown",
  async ({ field, warehouseId }: { field: string; warehouseId?: string }) => {
    const response: any = await Axios.get(
      COMPLETED_BASE.FILTER_DROPDOWN(field, warehouseId),
    );
    return response?.data;
  },
);

export const fetchGRNDetail = createAsyncThunk(
  "returnedAccessoriesInward/fetchGRNDetail",
  async (inwardId: string) => {
    const response: any = await Axios.get(COMPLETED_BASE.GRN_DETAIL(inwardId));
    return response?.data;
  },
);
export const revertInward = createAsyncThunk(
  "returnedAccessoriesInward/revertInward",
  async (inwardId: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(COMPLETED_BASE.REVERT(inwardId));
      return response?.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to revert inward");
    }
  },
);
export const editInward = createAsyncThunk(
  "returnedAccessoriesInward/editInward",
  async (
    { inwardId, formData }: { inwardId: string; formData: any },
    { rejectWithValue },
  ) => {
    try {
      const response: any = await Axios.put(COMPLETED_BASE.EDIT(inwardId), formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response?.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update inward");
    }
  },
);
